import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { workersAPI, contractorsAPI } from '../utils/api';
import toast from 'react-hot-toast';
import ConfirmationModal from '../components/ConfirmationModal';
import {
  UserGroupIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  PencilSquareIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  BuildingOfficeIcon,
  IdentificationIcon,
} from '@heroicons/react/24/outline';

const EMPLOYEE_TYPES = ['employee', 'contractor'];

const FILTER_TABS = [
  { label: 'Active', value: 'true' },
  { label: 'All', value: 'all' },
  { label: 'Inactive', value: 'false' },
];

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  badgeId: '',
  email: '',
  phone: '',
  employeeType: 'employee',
  contractorId: '',
  department: '',
  role: '',
  trade: '',
  hireDate: '',
};

function safetyScoreBadge(score) {
  if (score >= 80) return 'bg-emerald-100 text-emerald-700';
  if (score >= 60) return 'bg-amber-100 text-amber-700';
  return 'bg-red-100 text-red-700';
}

function WorkerFormModal({ isOpen, onClose, onSubmit, initial, contractors, isSubmitting }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setForm(
        initial
          ? {
              firstName: initial.firstName || '',
              lastName: initial.lastName || '',
              badgeId: initial.badgeId || '',
              email: initial.email || '',
              phone: initial.phone || '',
              employeeType: initial.employeeType || 'employee',
              contractorId: initial.contractorId || '',
              department: initial.department || '',
              role: initial.role || '',
              trade: initial.trade || '',
              hireDate: initial.hireDate ? initial.hireDate.split('T')[0] : '',
            }
          : EMPTY_FORM
      );
      setErrors({});
    }
  }, [isOpen, initial]);

  const set = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'Required';
    if (!form.lastName.trim()) e.lastName = 'Required';
    if (!form.badgeId.trim()) e.badgeId = 'Required';
    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Invalid email';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      badgeId: form.badgeId.trim(),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      employeeType: form.employeeType,
      contractorId: form.contractorId || null,
      department: form.department.trim() || null,
      role: form.role.trim() || null,
      trade: form.trade.trim() || null,
      hireDate: form.hireDate || null,
    };
    onSubmit(payload);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl z-10 max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900">
              {initial ? 'Edit Worker' : 'Add Worker'}
            </h2>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} className="overflow-y-auto flex-1">
            <div className="px-6 py-5 space-y-5">
              {/* Basic Info */}
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                  Basic Information
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.firstName}
                      onChange={(e) => set('firstName', e.target.value)}
                      placeholder="John"
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-blue-500 ${errors.firstName ? 'border-red-400 bg-red-50' : 'border-slate-300'}`}
                    />
                    {errors.firstName && <p className="mt-1 text-xs text-red-500">{errors.firstName}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.lastName}
                      onChange={(e) => set('lastName', e.target.value)}
                      placeholder="Doe"
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-blue-500 ${errors.lastName ? 'border-red-400 bg-red-50' : 'border-slate-300'}`}
                    />
                    {errors.lastName && <p className="mt-1 text-xs text-red-500">{errors.lastName}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Badge ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.badgeId}
                      onChange={(e) => set('badgeId', e.target.value)}
                      placeholder="e.g. W-1001"
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-blue-500 ${errors.badgeId ? 'border-red-400 bg-red-50' : 'border-slate-300'}`}
                    />
                    {errors.badgeId && <p className="mt-1 text-xs text-red-500">{errors.badgeId}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Hire Date</label>
                    <input
                      type="date"
                      value={form.hireDate}
                      onChange={(e) => set('hireDate', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => set('email', e.target.value)}
                      placeholder="john@example.com"
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-blue-500 ${errors.email ? 'border-red-400 bg-red-50' : 'border-slate-300'}`}
                    />
                    {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => set('phone', e.target.value)}
                      placeholder="+91 98765 43210"
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Role & Assignment */}
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                  Role & Assignment
                </p>
                <div className="space-y-4">
                  {/* Employee Type */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Employee Type</label>
                    <div className="flex gap-3">
                      {EMPLOYEE_TYPES.map((t) => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => set('employeeType', t)}
                          className={`flex-1 py-2 px-4 text-sm font-medium rounded-lg border-2 capitalize transition-colors ${
                            form.employeeType === t
                              ? 'border-blue-600 bg-blue-50 text-blue-700'
                              : 'border-slate-200 text-slate-500 hover:border-slate-300'
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Contractor picker — only for contractor type */}
                  {form.employeeType === 'contractor' && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Contractor Company
                      </label>
                      <select
                        value={form.contractorId}
                        onChange={(e) => set('contractorId', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500"
                      >
                        <option value="">— Select contractor —</option>
                        {contractors.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name} ({c.code})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Department</label>
                      <input
                        type="text"
                        value={form.department}
                        onChange={(e) => set('department', e.target.value)}
                        placeholder="e.g. Engineering"
                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Role / Title</label>
                      <input
                        type="text"
                        value={form.role}
                        onChange={(e) => set('role', e.target.value)}
                        placeholder="e.g. Site Engineer"
                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Trade</label>
                      <input
                        type="text"
                        value={form.trade}
                        onChange={(e) => set('trade', e.target.value)}
                        placeholder="e.g. Electrician"
                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3 bg-slate-50 rounded-b-xl">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors"
              >
                {isSubmitting ? 'Saving...' : initial ? 'Save Changes' : 'Add Worker'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function WorkersPage() {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('true');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingWorker, setEditingWorker] = useState(null);
  const [deactivatingWorker, setDeactivatingWorker] = useState(null);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const queryParams = {
    search: debouncedSearch || undefined,
    isActive: activeFilter !== 'all' ? activeFilter : undefined,
    limit: 100,
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['workers', queryParams],
    queryFn: () => workersAPI.getWorkers(queryParams).then((r) => r.data),
  });

  const { data: contractorsData } = useQuery({
    queryKey: ['contractors', { isActive: 'true' }],
    queryFn: () => contractorsAPI.getAll({ isActive: 'true' }).then((r) => r.data),
  });

  const workers = data?.data || [];
  const total = data?.pagination?.total || 0;
  const contractors = contractorsData?.data || [];

  const createMutation = useMutation({
    mutationFn: (payload) => workersAPI.createWorker(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workers'] });
      toast.success('Worker added successfully');
      setShowForm(false);
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Failed to add worker');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => workersAPI.updateWorker(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workers'] });
      toast.success('Worker updated successfully');
      setShowForm(false);
      setEditingWorker(null);
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Failed to update worker');
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (id) => workersAPI.deleteWorker(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workers'] });
      toast.success('Worker deactivated');
      setDeactivatingWorker(null);
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Failed to deactivate worker');
    },
  });

  const handleOpenCreate = () => {
    setEditingWorker(null);
    setShowForm(true);
  };

  const handleOpenEdit = (worker) => {
    setEditingWorker(worker);
    setShowForm(true);
  };

  const handleFormSubmit = (payload) => {
    if (editingWorker) {
      updateMutation.mutate({ id: editingWorker.id, payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleDeactivateConfirm = () => {
    if (deactivatingWorker) {
      deactivateMutation.mutate(deactivatingWorker.id);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Workers</h1>
          <p className="text-sm text-slate-500 mt-1">Manage worker onboarding and profiles</p>
        </div>
        <button
          onClick={handleOpenCreate}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 text-sm font-medium w-fit transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          Add Worker
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="glass p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center">
              <UserGroupIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xl font-black text-slate-800">{total}</p>
              <p className="text-xs text-slate-500">Total Workers</p>
            </div>
          </div>
        </div>
        <div className="glass p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
              <ShieldCheckIcon className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xl font-black text-slate-800">
                {workers.filter((w) => w.isActive).length}
              </p>
              <p className="text-xs text-slate-500">Active</p>
            </div>
          </div>
        </div>
        <div className="glass p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center">
              <BuildingOfficeIcon className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-xl font-black text-slate-800">
                {workers.filter((w) => w.employeeType === 'contractor').length}
              </p>
              <p className="text-xs text-slate-500">Contractors</p>
            </div>
          </div>
        </div>
        <div className="glass p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xl font-black text-slate-800">
                {workers.filter((w) => (w.safetyScore ?? 100) < 80).length}
              </p>
              <p className="text-xs text-slate-500">At Risk</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="glass p-4">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          {/* Search */}
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlassIcon className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name, badge, department..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm bg-slate-100 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400 focus:bg-white"
            />
          </div>

          {/* Filter tabs */}
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            {FILTER_TABS.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setActiveFilter(tab.value)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  activeFilter === tab.value
                    ? 'bg-white text-slate-800 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {isFetching && !isLoading && (
            <span className="text-xs text-slate-400">Updating...</span>
          )}
        </div>
      </div>

      {/* Table */}
      <div className={`glass p-0 overflow-hidden transition-opacity duration-200 ${isFetching && !isLoading ? 'opacity-60' : ''}`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/80">
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500">Worker</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500">Badge ID</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500">Department</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500">Trade</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500">Type</th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500">Safety Score</th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500">Violations</th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500">Status</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-slate-400 text-sm">
                    Loading workers...
                  </td>
                </tr>
              ) : workers.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center">
                    <UserGroupIcon className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-400">
                      {search ? 'No workers match your search' : 'No workers yet — add your first worker'}
                    </p>
                  </td>
                </tr>
              ) : (
                workers.map((worker) => (
                  <tr
                    key={worker.id}
                    className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                  >
                    {/* Worker */}
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-600 shrink-0">
                          {worker.firstName[0]}{worker.lastName[0]}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">
                            {worker.firstName} {worker.lastName}
                          </p>
                          {worker.role && (
                            <p className="text-xs text-slate-400">{worker.role}</p>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Badge */}
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs font-mono">
                        <IdentificationIcon className="w-3 h-3" />
                        {worker.badgeId}
                      </span>
                    </td>

                    {/* Department */}
                    <td className="py-3 px-4 text-slate-500 text-sm">
                      {worker.department || <span className="text-slate-300">—</span>}
                    </td>

                    {/* Trade */}
                    <td className="py-3 px-4 text-slate-500 text-sm">
                      {worker.trade || <span className="text-slate-300">—</span>}
                    </td>

                    {/* Type */}
                    <td className="py-3 px-4">
                      {worker.employeeType === 'contractor' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">
                          <BuildingOfficeIcon className="w-3 h-3" />
                          {worker.contractor?.name || 'Contractor'}
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                          Employee
                        </span>
                      )}
                    </td>

                    {/* Safety Score */}
                    <td className="py-3 px-4 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${safetyScoreBadge(worker.safetyScore ?? 100)}`}>
                        {worker.safetyScore ?? 100}
                      </span>
                    </td>

                    {/* Violations */}
                    <td className="py-3 px-4 text-center">
                      <span className={`text-sm font-bold ${worker.totalViolations > 10 ? 'text-red-600' : worker.totalViolations > 5 ? 'text-amber-600' : 'text-slate-500'}`}>
                        {worker.totalViolations}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="py-3 px-4 text-center">
                      {worker.isActive ? (
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full text-xs font-medium">
                          Inactive
                        </span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleOpenEdit(worker)}
                          className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit worker"
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </button>
                        {worker.isActive && (
                          <button
                            onClick={() => setDeactivatingWorker(worker)}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Deactivate worker"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination info */}
        {!isLoading && workers.length > 0 && (
          <div className="px-4 py-3 border-t border-slate-100 text-xs text-slate-400">
            Showing {workers.length} of {total} workers
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      <WorkerFormModal
        isOpen={showForm}
        onClose={() => { setShowForm(false); setEditingWorker(null); }}
        onSubmit={handleFormSubmit}
        initial={editingWorker}
        contractors={contractors}
        isSubmitting={isSubmitting}
      />

      {/* Deactivate Confirmation */}
      <ConfirmationModal
        isOpen={!!deactivatingWorker}
        onClose={() => setDeactivatingWorker(null)}
        onConfirm={handleDeactivateConfirm}
        title="Deactivate Worker"
        message={`Are you sure you want to deactivate ${deactivatingWorker?.firstName} ${deactivatingWorker?.lastName}? They will no longer appear in active worker lists.`}
        confirmLabel={deactivateMutation.isPending ? 'Deactivating...' : 'Deactivate'}
        variant="danger"
      />
    </div>
  );
}
